import {escapeHtml} from "../services/translations";
import {getTranslation} from "./helpers";
import utils from "../utils/utils";
import {getSiteVar} from "../services/site-vars";
import {clearCloudAuthFromSession, readCloudAuthFromSession, writeCloudAuthToSession} from "./cloud-auth-storage";

const customerPopupAuthAction = 'wl_get_customer_popup_cloud_auth';
const customerPopupAuthRefreshSkewMs = 5 * 1000;
const customerPopupRequestTimeoutMs = 120 * 1000;
const customerPopupCloudAuthMap = new Map();
const customerPopupCloudAuthPromiseMap = new Map();

function createCustomerPopupCloudError(message, code = 'customer_popup_cloud_error', extra = {}) {
    const error = new Error(String(message || getTranslation('customer_load_error')));
    error.code = code;
    Object.assign(error, extra);
    return error;
}

function ajaxPromise(ajaxSettings = {}) {
    return new Promise((resolve, reject) => {
        $.ajax({
            ...ajaxSettings,
            success: (response, textStatus, jqXHR) => resolve({response, textStatus, jqXHR}),
            error: (xhr, status, error) => reject({xhr, status, error}),
        });
    });
}

function isWpNonceFailureResponse(jqXHR) {
    if (!jqXHR || Number.parseInt(jqXHR.status, 10) !== 403) {
        return false;
    }

    return String(jqXHR.responseText || '').trim() === '-1';
}

async function ajaxWithFreshNonceRetry(ajaxSettingsBuilder) {
    const request = () => ajaxPromise(ajaxSettingsBuilder());

    try {
        return await request();
    } catch (err) {
        if (!isWpNonceFailureResponse(err?.xhr)) {
            throw err;
        }

        await utils.fetch_fresh_nonce(true);
        return request();
    }
}

function normalizeCustomerPopupAuthResponse(response = null) {
    if (!response || response.success !== true || !response.data || typeof response.data !== 'object') {
        return null;
    }

    if (response.data.enabled !== true) {
        return {
            enabled: false,
        };
    }

    const functionUrl = String(response.data.function_url || '').trim();
    const authToken = String(response.data.auth_token || '').trim();
    const expiresAt = Number.parseInt(response.data.expires_at, 10) || 0;
    const requestPayload = response.data.request_payload && typeof response.data.request_payload === 'object'
        ? response.data.request_payload
        : {};

    if (!functionUrl || !authToken || expiresAt <= 0) {
        return null;
    }

    return normalizeStoredCustomerPopupAuth({
        functionUrl,
        authToken,
        expiresAtMs: expiresAt * 1000,
        requestPayload,
    });
}

function normalizeStoredCustomerPopupAuth(authPayload = null) {
    if (!authPayload || typeof authPayload !== 'object') {
        return null;
    }

    const functionUrl = String(authPayload.functionUrl || '').trim();
    const authToken = String(authPayload.authToken || '').trim();
    const expiresAtMs = Number.parseInt(authPayload.expiresAtMs, 10) || 0;
    const requestPayload = authPayload.requestPayload && typeof authPayload.requestPayload === 'object'
        ? {...authPayload.requestPayload}
        : {};

    if (!functionUrl || !authToken || expiresAtMs <= 0) {
        return null;
    }

    return {
        enabled: true,
        functionUrl,
        authToken,
        expiresAtMs,
        requestPayload,
    };
}

function normalizeCustomerPopupCloudResponse(response = null) {
    if (
        response &&
        typeof response === 'object' &&
        !Array.isArray(response) &&
        response.success === true &&
        typeof response.html === 'string' &&
        response.html.trim() !== ''
    ) {
        return response;
    }

    const message = response && typeof response === 'object'
        ? String(response.message || response.data?.message || '')
        : '';

    throw createCustomerPopupCloudError(
        message || 'Customer data cloud request failed.',
        'customer_popup_cloud_invalid_response',
    );
}

function normalizeCustomerPopupRequestError(err) {
    if (err instanceof Error && err.code) {
        return err;
    }

    if (err?.xhr?.status === 401 || err?.xhr?.status === 403) {
        return createCustomerPopupCloudError(
            getTranslation('customer_access_denied'),
            'customer_popup_cloud_unauthorized',
            {cause: err},
        );
    }

    if (err?.error) {
        return createCustomerPopupCloudError(
            String(err.error),
            'customer_popup_cloud_request_failed',
            {cause: err},
        );
    }

    return createCustomerPopupCloudError(
        err?.message || getTranslation('customer_load_error'),
        'customer_popup_cloud_request_failed',
        {cause: err},
    );
}

function buildCustomerPopupRequestData(data = {}) {
    const requestData = data && typeof data === 'object' ? {...data} : {};
    requestData._wpnonce = utils.get_nonce();

    if (!requestData.post_id) {
        requestData.post_id = utils.get_post_id();
    }

    return requestData;
}

function getCustomerPopupAuthContext(baseData = {}) {
    return {
        currentUserId: Number.parseInt(getSiteVar('current_user_id'), 10) || 0,
        customerUserId: Number.parseInt(baseData.user_id, 10) || 0,
        postId: Number.parseInt(baseData.post_id, 10) || 0,
        nonce: String(baseData._wpnonce || ''),
    };
}

function getCustomerPopupAuthContextKey(context = {}) {
    return [
        'customer_popup',
        'v3',
        Number.parseInt(context.currentUserId, 10) || 0,
        Number.parseInt(context.customerUserId, 10) || 0,
        Number.parseInt(context.postId, 10) || 0,
        String(context.nonce || ''),
    ].join(':');
}

function hasFreshCustomerPopupAuth(authPayload = null) {
    const expiresAtMs = Number.parseInt(authPayload?.expiresAtMs, 10) || 0;
    if (!authPayload || !authPayload.authToken || !authPayload.functionUrl || expiresAtMs <= 0) {
        return false;
    }

    return expiresAtMs > (Date.now() + customerPopupAuthRefreshSkewMs);
}

function clearCustomerPopupAuth(context) {
    const contextKey = getCustomerPopupAuthContextKey(context);
    customerPopupCloudAuthMap.delete(contextKey);
    customerPopupCloudAuthPromiseMap.delete(contextKey);
    clearCloudAuthFromSession({
        bootstrapAction: customerPopupAuthAction,
        contextKey,
    });
}

async function fetchCustomerPopupCloudAuth(baseData = {}, forceRefresh = false) {
    const context = getCustomerPopupAuthContext(baseData);
    const contextKey = getCustomerPopupAuthContextKey(context);

    if (forceRefresh) {
        clearCustomerPopupAuth(context);
    }

    if (!customerPopupCloudAuthMap.has(contextKey)) {
        const storedAuth = readCloudAuthFromSession({
            bootstrapAction: customerPopupAuthAction,
            contextKey,
            normalizeAuthPayload: normalizeStoredCustomerPopupAuth,
        });

        if (storedAuth) {
            customerPopupCloudAuthMap.set(contextKey, storedAuth);
        }
    }

    const currentAuth = customerPopupCloudAuthMap.get(contextKey) || null;
    if (hasFreshCustomerPopupAuth(currentAuth)) {
        return currentAuth;
    }

    if (customerPopupCloudAuthPromiseMap.has(contextKey)) {
        return customerPopupCloudAuthPromiseMap.get(contextKey);
    }

    const requestAuth = async () => {
        const {response: authResponse} = await ajaxWithFreshNonceRetry(() => ({
            type: 'POST',
            url: utils.get_ajax_url(),
            dataType: 'json',
            wl_skip_nonce_retry: true,
            data: {
                ...baseData,
                action: customerPopupAuthAction,
                _wpnonce: utils.get_nonce(),
            },
        }));
        const cloudAuth = normalizeCustomerPopupAuthResponse(authResponse);
        if (cloudAuth && cloudAuth.enabled === false) {
            throw createCustomerPopupCloudError(
                'Customer popup cloud is not configured.',
                'customer_popup_cloud_disabled',
            );
        }
        if (!cloudAuth) {
            throw createCustomerPopupCloudError(
                'Failed to initialize customer popup cloud access.',
                'customer_popup_cloud_bootstrap_failed',
            );
        }

        customerPopupCloudAuthMap.set(contextKey, cloudAuth);
        writeCloudAuthToSession({
            bootstrapAction: customerPopupAuthAction,
            contextKey,
            authPayload: cloudAuth,
            normalizeAuthPayload: normalizeStoredCustomerPopupAuth,
        });

        return cloudAuth;
    };

    const authPromise = requestAuth()
        .finally(() => {
            customerPopupCloudAuthPromiseMap.delete(contextKey);
        });

    customerPopupCloudAuthPromiseMap.set(contextKey, authPromise);
    return authPromise;
}

function isCloudUnauthorized(err) {
    return !!(err?.xhr && (err.xhr.status === 401 || err.xhr.status === 403));
}

function buildCustomerPopupCloudRequestPayload(baseData = {}) {
    const userId = Number.parseInt(baseData.user_id, 10) || 0;
    const postId = Number.parseInt(baseData.post_id, 10) || 0;

    return {
        user_id: userId,
        post_id: postId || null,
    };
}

async function requestCustomerPopupRelatedData(cloudAuth, baseData = {}) {
    const {response: cloudResponse} = await ajaxPromise({
        type: 'POST',
        url: cloudAuth.functionUrl,
        dataType: 'json',
        crossDomain: true,
        timeout: customerPopupRequestTimeoutMs,
        contentType: 'application/json',
        processData: false,
        data: JSON.stringify({
            ...cloudAuth.requestPayload,
            ...buildCustomerPopupCloudRequestPayload(baseData),
            auth_token: cloudAuth.authToken,
        }),
    });

    return normalizeCustomerPopupCloudResponse(cloudResponse);
}

export function renderCustomerPopupCloudErrorHtml() {
    return '<div class="grid-container"><div class="grid-x"><div class="small-12 cell"><p>' + escapeHtml(getTranslation('generic_retry')) + '</p></div></div></div>';
}

export async function requestCustomerPopupCloudHtml(data = {}) {
    const baseData = buildCustomerPopupRequestData(data);

    try {
        let cloudAuth = await fetchCustomerPopupCloudAuth(baseData);
        let relatedData;

        try {
            relatedData = await requestCustomerPopupRelatedData(cloudAuth, baseData);
        } catch (err) {
            if (!isCloudUnauthorized(err)) {
                throw err;
            }

            cloudAuth = await fetchCustomerPopupCloudAuth(baseData, true);
            relatedData = await requestCustomerPopupRelatedData(cloudAuth, baseData);
        }

        return relatedData.html;
    } catch (err) {
        throw normalizeCustomerPopupRequestError(err);
    }
}
